import axios, { AxiosError } from 'axios';
import { useAuthStore } from '../store/auth';

// API base URL - detect environment
const getBaseUrl = () => {
  const hostname = window.location.hostname;
  console.log('[API] Hostname:', hostname);

  // In production, use relative path (proxy handles it)
  if (hostname === 'affiliate-admin.worldreach.io') {
    const baseUrl = '/api/v1';
    console.log('[API] Using production URL:', baseUrl);
    return baseUrl;
  }

  // In development, use relative path (Vite proxy handles it)
  console.log('[API] Using development proxy');
  return '/api/v1';
};

const api = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
    'X-Tenant-ID': 'demo-casino', // Default tenant
  },
  timeout: 30000, // 30 second timeout
});

// User-friendly error messages
const getErrorMessage = (error: AxiosError<{ error?: string; message?: string }>): string => {
  // Network errors
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return 'Request timed out. Please try again.';
    }
    return 'Unable to connect to server. Please check your internet connection.';
  }

  const status = error.response.status;
  const serverMessage = error.response.data?.error || error.response.data?.message;

  // Handle specific HTTP status codes
  switch (status) {
    case 400:
      return serverMessage || 'Invalid request. Please check your input.';
    case 401:
      return serverMessage || 'Invalid email or password.';
    case 403:
      return 'Access denied. You do not have permission to perform this action.';
    case 404:
      return 'Service temporarily unavailable. Please try again later.';
    case 409:
      return serverMessage || 'This record already exists.';
    case 422:
      return serverMessage || 'Please check the form for errors.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
      return 'Server error. Please try again later.';
    case 502:
    case 503:
    case 504:
      return 'Service temporarily unavailable. Please try again later.';
    default:
      return serverMessage || 'An unexpected error occurred. Please try again.';
  }
};

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    console.log('[API Request]', config.method?.toUpperCase(), config.url);
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Handle responses and token refresh
api.interceptors.response.use(
  (response) => {
    console.log('[API Response]', response.status, response.config.url);
    return response;
  },
  async (error: AxiosError) => {
    console.error('[API Error]', error.response?.status, error.config?.url, error.message);
    const originalRequest = error.config as typeof error.config & { _retry?: boolean };

    // Handle 401 with token refresh
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Don't retry for login requests
      if (originalRequest.url?.includes('/auth/admin/login')) {
        const friendlyError = new Error(getErrorMessage(error as AxiosError<{ error?: string; message?: string }>));
        return Promise.reject(friendlyError);
      }

      originalRequest._retry = true;

      try {
        await useAuthStore.getState().refreshAuth();
        const { accessToken } = useAuthStore.getState();
        if (accessToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return api(originalRequest);
      } catch {
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    // Transform error to user-friendly message
    const friendlyError = new Error(getErrorMessage(error as AxiosError<{ error?: string; message?: string }>));
    (friendlyError as any).originalError = error;
    (friendlyError as any).status = error.response?.status;
    return Promise.reject(friendlyError);
  }
);

export default api;
